import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AuthContext from '../../context/authContext';
import useFoodOrderKitchen from '../../hooks/useFoodOrderKitchen';
import OrderCardPreview from '../components/OrderCardPreview';

// Slug used in the downloaded file names (ASCII, kebab-case).
const fileNamePart = value =>
  String(value || '')
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'bestell-qr';

const saveBlob = (blob, fileName, type) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// One code: a preview of the printable card together with its PDF download. The
// PDF itself is rendered by the backend so the printed QR keeps full resolution.
const OrderQrCard = ({ title, subtitle, link, busy, onDownload }) => (
  <Card sx={{ width: 240 }}>
    <CardContent>
      <Stack spacing={1.5} alignItems='center'>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        <OrderCardPreview link={link} />
        {subtitle && (
          <Typography variant='caption' color='text.secondary' align='center'>
            {subtitle}
          </Typography>
        )}
        <Button
          variant='outlined'
          size='small'
          startIcon={busy ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          disabled={busy}
          onClick={onDownload}
        >
          PDF herunterladen
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

// Download view for the order QR codes of the online menu. Every location has a
// general code that works without any configuration. Where tables are maintained
// (per entertainment via maxTableNumber + unusableTables) there is additionally
// one code per table: its link carries the table number invisibly, so orders
// scanned at the table are assigned to it without the customer seeing a table
// (no table service is suggested).
const OrderQrCodes = () => {
  const authCtx = useContext(AuthContext);
  const canView =
    authCtx.checkRight('foodOrder:read') || authCtx.checkRight('foodOrder:read:own');

  const { getOrderQrOverview, getBranchOrderQrPdf, getTableQrPdf, getTableQrZip } =
    useFoodOrderKitchen();

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedEntertainmentId, setSelectedEntertainmentId] = useState('');
  // Key of the download currently running ('general', 'all', 'zip' or a table number).
  const [busyKey, setBusyKey] = useState(null);

  const getOrderQrOverviewRef = useRef(getOrderQrOverview);
  useEffect(() => {
    getOrderQrOverviewRef.current = getOrderQrOverview;
  }, [getOrderQrOverview]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOrderQrOverviewRef.current();
        if (cancelled) return;
        const list = data?.branches || [];
        setBranches(list);
        // A user assigned to exactly one location lands directly on it; everyone
        // else picks via the dropdown.
        if (list.length === 1) setSelectedBranchId(list[0].branchId);
      } catch (_) {
        if (!cancelled) toast.error('Fehler beim Laden der Standorte');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const selectedBranch = useMemo(
    () => branches.find(b => b.branchId === selectedBranchId) || null,
    [branches, selectedBranchId]
  );

  // Auto-select the entertainment when the branch has exactly one with tables.
  useEffect(() => {
    const entertainments = selectedBranch?.entertainments || [];
    setSelectedEntertainmentId(entertainments.length === 1 ? entertainments[0]._id : '');
  }, [selectedBranch]);

  const selectedEntertainment = useMemo(
    () =>
      (selectedBranch?.entertainments || []).find(e => e._id === selectedEntertainmentId) ||
      null,
    [selectedBranch, selectedEntertainmentId]
  );

  const tables = useMemo(() => {
    if (!selectedEntertainment) return [];
    const unusable = new Set((selectedEntertainment.unusableTables || []).map(Number));
    const result = [];
    for (let i = 1; i <= (selectedEntertainment.maxTableNumber || 0); i++) {
      result.push({ number: i, unusable: unusable.has(i) });
    }
    return result;
  }, [selectedEntertainment]);

  const hasUsableTables = tables.some(t => !t.unusable);
  const hasTableEntertainments = (selectedBranch?.entertainments || []).length > 0;

  const baseFileName = useMemo(
    () => (selectedBranch ? `bestell-qr-${fileNamePart(selectedBranch.locationCode)}` : 'bestell-qr'),
    [selectedBranch]
  );

  // The general card of the location: same print product, but with a code that
  // carries no table. It is offered for every location, including those without
  // any table configuration.
  const downloadGeneralPdf = async () => {
    if (!selectedBranch) return;
    setBusyKey('general');
    try {
      const blob = await getBranchOrderQrPdf(selectedBranch.branchId);
      // null = the request was stored for rights escalation; nothing to save yet.
      if (blob) saveBlob(blob, `${baseFileName}-allgemein.pdf`, 'application/pdf');
    } catch (_) {
      toast.error('Fehler beim Erstellen des allgemeinen QR-Codes');
    } finally {
      setBusyKey(null);
    }
  };

  const downloadPdf = async tableNumber => {
    if (!selectedEntertainment) return;
    setBusyKey(tableNumber ?? 'all');
    try {
      const blob = await getTableQrPdf(selectedEntertainment._id, tableNumber);
      if (blob) {
        const suffix = tableNumber
          ? `tisch-${tableNumber}`
          : fileNamePart(selectedEntertainment.name);
        saveBlob(blob, `${baseFileName}-${suffix}.pdf`, 'application/pdf');
      }
    } catch (_) {
      toast.error('Fehler beim Erstellen der Tisch-PDFs');
    } finally {
      setBusyKey(null);
    }
  };

  const downloadZip = async () => {
    if (!selectedEntertainment) return;
    setBusyKey('zip');
    try {
      const blob = await getTableQrZip(selectedEntertainment._id);
      if (blob) {
        saveBlob(
          blob,
          `${baseFileName}-${fileNamePart(selectedEntertainment.name)}.zip`,
          'application/zip'
        );
      }
    } catch (_) {
      toast.error('Fehler beim Erstellen des ZIP-Archivs');
    } finally {
      setBusyKey(null);
    }
  };

  if (!canView) {
    return <Navigate to='/' replace />;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
        <QrCode2Icon color='secondary' />
        <Typography variant='h5' sx={{ fontWeight: 700 }}>
          Bestell-QR-Codes
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Druckfertige Karten mit dem QR-Code zur Online-Speisekarte. Jeder Standort hat einen
        allgemeinen Code, der immer funktioniert. Wo Tische gepflegt sind, gibt es zusätzlich
        einen Code je Tisch: Bestellungen darüber werden automatisch dem jeweiligen Tisch
        zugeordnet — für den Kunden unsichtbar, damit keine Lieferung an den Tisch erwartet
        wird. Die Küche sieht die Tischnummer wie gewohnt.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress />
        </Box>
      ) : branches.length === 0 ? (
        <Alert severity='info'>Für dich sind keine Standorte freigegeben.</Alert>
      ) : (
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ minWidth: 260 }}>
              <InputLabel id='order-qr-branch-label'>Standort</InputLabel>
              <Select
                labelId='order-qr-branch-label'
                label='Standort'
                value={selectedBranchId}
                onChange={e => setSelectedBranchId(e.target.value)}
              >
                {branches.map(b => (
                  <MenuItem key={b.branchId} value={b.branchId}>
                    {b.branchName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedBranch && (selectedBranch.entertainments || []).length > 1 && (
              <FormControl sx={{ minWidth: 260 }}>
                <InputLabel id='order-qr-entertainment-label'>Entertainment</InputLabel>
                <Select
                  labelId='order-qr-entertainment-label'
                  label='Entertainment'
                  value={selectedEntertainmentId}
                  onChange={e => setSelectedEntertainmentId(e.target.value)}
                >
                  {selectedBranch.entertainments.map(e => (
                    <MenuItem key={e._id} value={e._id}>
                      {e.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedEntertainment && (
              <Stack
                direction='row'
                spacing={1}
                sx={{ alignSelf: { sm: 'center' }, flexWrap: 'wrap' }}
              >
                <Button
                  variant='contained'
                  startIcon={
                    busyKey === 'all' ? <CircularProgress size={16} /> : <PictureAsPdfIcon />
                  }
                  onClick={() => downloadPdf()}
                  disabled={!hasUsableTables || busyKey !== null}
                >
                  Alle Tische als PDF
                </Button>
                <Button
                  variant='outlined'
                  startIcon={
                    busyKey === 'zip' ? <CircularProgress size={16} /> : <FolderZipIcon />
                  }
                  onClick={downloadZip}
                  disabled={!hasUsableTables || busyKey !== null}
                >
                  Alle Tische als ZIP
                </Button>
              </Stack>
            )}
          </Stack>

          {!selectedBranch && <Alert severity='info'>Bitte wähle einen Standort aus.</Alert>}

          {selectedBranch && (
            <>
              <Box>
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                  Allgemeiner Code
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Führt zur Speisekarte von {selectedBranch.branchName}, ohne Tischzuordnung —
                  einsetzbar überall im Haus (Tresen, Flyer, Aufsteller).
                </Typography>
                <OrderQrCard
                  title='Allgemein'
                  subtitle='Ohne Tischzuordnung'
                  link={selectedBranch.menuUrl}
                  busy={busyKey === 'general'}
                  onDownload={downloadGeneralPdf}
                />
              </Box>

              <Divider />

              {!hasTableEntertainments ? (
                <Alert severity='info'>
                  Für diesen Standort sind keine Tische hinterlegt — hier gibt es nur den
                  allgemeinen Code. Tische werden pro Entertainment in der
                  Entertainment-Verwaltung gepflegt (Feld &quot;Maximale Tischnummer&quot;).
                </Alert>
              ) : !selectedEntertainment ? (
                <Alert severity='info'>
                  Bitte wähle ein Entertainment aus, um die Tisch-Codes zu sehen.
                </Alert>
              ) : (
                <Box>
                  <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                    Tisch-Codes
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                    {selectedEntertainment.name} — Tische 1 bis{' '}
                    {selectedEntertainment.maxTableNumber}. &quot;Alle Tische als PDF&quot;
                    liefert ein Dokument mit einer Karte pro Seite, &quot;Alle Tische als
                    ZIP&quot; ein PDF je Tisch.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {tables.map(t =>
                      t.unusable ? (
                        // Defekte Tische bekommen keinen Code: sie werden bei der
                        // Bestellung ohnehin verworfen.
                        <Card key={t.number} sx={{ width: 240, opacity: 0.55 }}>
                          <CardContent>
                            <Stack spacing={1.5} alignItems='center' sx={{ py: 4 }}>
                              <Typography sx={{ fontWeight: 700 }}>Tisch {t.number}</Typography>
                              <Chip size='small' label='defekt' />
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                align='center'
                              >
                                Als defekt markiert — kein QR-Code verfügbar.
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      ) : (
                        <OrderQrCard
                          key={t.number}
                          title={`Tisch ${t.number}`}
                          link={`${selectedBranch.menuUrl}&t=${t.number}`}
                          busy={busyKey === t.number}
                          onDownload={() => downloadPdf(t.number)}
                        />
                      )
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default OrderQrCodes;
